import { OrderEntity } from "../../Objects/Order/OrderEntity"
import { useTranslation } from "react-i18next"

interface PaymentDetailsProps {
  order: OrderEntity
}

const Item = ({
  label,
  value,
  color = "text-gray-900",
  icon,
}: {
  label: string
  value: string
  color?: string
  icon: React.ReactNode
}) => (
  <div className="flex flex-col items-center gap-1 text-center">
    <div className="flex items-center gap-1 text-gray-400 uppercase text-xs font-black tracking-widest">
      {icon}
      <span>{label}</span>
    </div>

    <div className={`font-bold text-sm md:text-base ${color}`}>{value}</div>
  </div>
)

export const PaymentDetails = ({ order }: PaymentDetailsProps) => {
  const { t } = useTranslation()
  
  const payment = order.orderPaymentEntity
  
  const hasCouponApplied = payment.totalCupom > 0
  
  const couponCode = order.couponEntity?.code ?? order.couponCode
  
  const couponValue = hasCouponApplied ? `- ${payment.totalCupom.toMoney()}`
    : t("publicMenu:payment_details.not_applied")

  return (
    <div className="w-full pb-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
      <Item
        label={t("publicMenu:payment_details.base_total")}
        value={payment.totalNotLiquid.toMoney()}
        icon={
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8c-3 0-5 1.5-5 3s2 3 5 3 5 1.5 5 3-2 3-5 3m0-12v12"
            />
          </svg>
        }
      />

      <Item
        label={t("publicMenu:payment_details.additionals")}
        value={payment.totalAddtionals.toMoney()}
        icon={
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        }
      />

      <Item
        label={t("publicMenu:payment_details.discount")}
        value={`- ${payment.totalDiscount.toMoney()}`}
        color="text-red-500"
        icon={
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
          </svg>
        }
      />

      <Item
        label={t("publicMenu:payment_details.coupon")}
        value={couponValue}
        color={hasCouponApplied ? "text-emerald-600" : "text-gray-500"}
        icon={
          <svg
            className="w-4 h-4 text-emerald-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l2 2 4-4" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 4h10l3 3v10l-3 3H7l-3-3V7l3-3z" />
          </svg>
        }
      />

      <Item
        label={t("publicMenu:payment_details.shipping")}
        value={payment.shippingFee === 0 ? t("publicMenu:payment_details.free") : payment.shippingFee.toMoney()}
        icon={
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
          </svg>
        }
      />

      <Item
        label={t("publicMenu:checkout.award_label")}
        value={payment.totalAward.toMoney()}
        color="text-amber-500"
        icon={
          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4M7 4h10l-1 7a4 4 0 01-8 0L7 4z" />
          </svg>
        }
      />
    </div>
  )
}
